import { log } from "./logging"
import { Message } from "./messages"
import { Resampler } from "./resampler"

export type ProcessorType = "AudioWorklet" | "ScriptProcessor" | "auto"

export type FrameHandler = (frame: Float32Array) => Promise<void>

const workletFile = "vad.worklet.bundle.min.js"

export interface AudioFrameSourceOptions {
  /** Frame size to emit, which has to match the model being fed. */
  frameSamples: number
  getStream: () => Promise<MediaStream>
  pauseStream: (stream: MediaStream) => Promise<void>
  resumeStream: (stream: MediaStream) => Promise<MediaStream>
  audioContext?: AudioContext
  processorType: ProcessorType
  workletOptions: AudioWorkletNodeOptions
  baseAssetPath: string
}

const micConstraints: MediaStreamConstraints = {
  audio: {
    channelCount: 1,
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: true,
  },
}

export const defaultStreamOptions = {
  getStream: async () =>
    await navigator.mediaDevices.getUserMedia(micConstraints),
  pauseStream: async (_stream: MediaStream) => {
    _stream.getTracks().forEach((track) => {
      track.stop()
    })
  },
  resumeStream: async () =>
    await navigator.mediaDevices.getUserMedia(micConstraints),
}

export const defaultAudioFrameSourceOptions = (
  frameSamples: number
): AudioFrameSourceOptions => ({
  frameSamples,
  ...defaultStreamOptions,
  processorType: "auto",
  workletOptions: {},
  baseAssetPath: "./",
})

const detectProcessorType = (
  ctx: AudioContext
): "AudioWorklet" | "ScriptProcessor" => {
  if ("audioWorklet" in ctx && typeof AudioWorkletNode === "function") {
    return "AudioWorklet"
  }
  return "ScriptProcessor"
}

async function getVADNodeAsWorklet(
  workletURL: string,
  workletOptions: AudioWorkletNodeOptions,
  audioContext: AudioContext,
  frameSamples: number,
  processFrame: FrameHandler
): Promise<AudioWorkletNode> {
  await audioContext.audioWorklet.addModule(workletURL)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  workletOptions.processorOptions = {
    ...(workletOptions.processorOptions ?? {}),
    frameSamples: frameSamples,
  }

  const audioNode = new AudioWorkletNode(
    audioContext,
    "vad-helper-worklet",
    workletOptions
  )
  audioNode.port.onmessage = async (ev: MessageEvent) => {
    const data: unknown = ev.data

    if (!(typeof data === "object" && data && "message" in data)) {
      console.error("Invalid message event", data)
      return
    }

    switch (data.message) {
      case Message.AudioFrame: {
        if (!("data" in data && data.data instanceof ArrayBuffer)) {
          console.log("Audio frame message has no data")
          return
        }
        const frame = new Float32Array(data.data)
        await processFrame(frame)
        break
      }
    }
  }

  return audioNode
}

async function getVADNodeAsScriptProcessor(
  audioContext: AudioContext,
  frameSamples: number,
  processFrame: FrameHandler
): Promise<ScriptProcessorNode> {
  const resampler = new Resampler({
    nativeSampleRate: audioContext.sampleRate,
    targetSampleRate: 16000, // VAD models expect 16kHz
    targetFrameSize: frameSamples,
  })
  log.debug("using script processor")

  // Fallback to ScriptProcessor
  const bufferSize = 4096 // Increased for more stable processing
  const audioNode = audioContext.createScriptProcessor(bufferSize, 1, 1)

  let processingAudio = false

  audioNode.onaudioprocess = async (e: AudioProcessingEvent) => {
    if (processingAudio) return
    processingAudio = true

    try {
      const input = e.inputBuffer.getChannelData(0)
      const output = e.outputBuffer.getChannelData(0)
      output.fill(0)

      // Process through resampler
      const frames = resampler.process(input)
      for (const frame of frames) {
        await processFrame(frame)
      }
    } catch (error) {
      console.error("Error processing audio:", error)
    } finally {
      processingAudio = false
    }
  }

  // https://github.com/WebAudio/web-audio-api/issues/345
  // -> we need to connect an output or will not work due to chrome bug
  audioNode.connect(audioContext.destination)

  return audioNode
}

/**
 * Owns the microphone, the AudioContext and the node graph, and emits 16 kHz
 * frames. It knows nothing about models or segmentation, so a single source
 * can feed several consumers — which is what lets two sets of
 * FrameProcessorOptions be compared on the identical audio.
 */
export class AudioFrameSource {
  private frameHandlers: FrameHandler[] = []

  private constructor(
    public options: AudioFrameSourceOptions,
    public listening = false,
    public errored: string | null = null,
    private _stream: MediaStream | null = null,
    private _audioContext: AudioContext | null = null,
    private _vadNode: AudioWorkletNode | ScriptProcessorNode | null = null,
    private _mediaStreamAudioSourceNode: MediaStreamAudioSourceNode | null = null,
    private _audioProcessorAdapterType:
      | "AudioWorklet"
      | "ScriptProcessor"
      | null = null,
    private initializationState:
      | "uninitialized"
      | "initializing"
      | "initialized"
      | "destroyed"
      | "errored" = "uninitialized",
    private ownsAudioContext = false
  ) {}

  /**
   * Nothing here touches the microphone. The stream is not requested until
   * `start` is called.
   */
  static async new(
    options: Partial<AudioFrameSourceOptions> & { frameSamples: number }
  ): Promise<AudioFrameSource> {
    const fullOptions: AudioFrameSourceOptions = {
      ...defaultAudioFrameSourceOptions(options.frameSamples),
      ...options,
    }
    return new AudioFrameSource(fullOptions)
  }

  /** Frames go to every registered handler, in registration order. */
  onFrame = (handler: FrameHandler) => {
    this.frameHandlers.push(handler)
  }

  private emitFrame = async (frame: Float32Array) => {
    for (const handler of this.frameHandlers) {
      await handler(frame)
    }
  }

  private getAudioInstances = (): {
    stream: MediaStream
    audioContext: AudioContext
    vadNode: AudioWorkletNode | ScriptProcessorNode
    mediaStreamAudioSourceNode: MediaStreamAudioSourceNode
  } => {
    if (
      this._stream === null ||
      this._audioContext === null ||
      this._vadNode == null ||
      this._mediaStreamAudioSourceNode == null
    ) {
      throw new Error(
        "AudioFrameSource has null stream, audio context, or processor adapter"
      )
    }
    return {
      stream: this._stream,
      audioContext: this._audioContext,
      vadNode: this._vadNode,
      mediaStreamAudioSourceNode: this._mediaStreamAudioSourceNode,
    }
  }

  setErrored = (error: string) => {
    this.initializationState = "errored"
    this.errored = error
  }

  start = async () => {
    switch (this.initializationState) {
      case "uninitialized": {
        log.debug("initializing audio frame source")
        this.initializationState = "initializing"

        try {
          this._stream = await this.options.getStream()
        } catch (error) {
          if (error instanceof Error) {
            this.setErrored(error.message)
          } else {
            this.setErrored(String(error))
          }
          throw error
        }
        if (this.options.audioContext) {
          console.log("using custom audio context")
          this._audioContext = this.options.audioContext
        } else {
          console.log("using default audio context")
          this._audioContext = new AudioContext()
          this.ownsAudioContext = true
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!this._audioContext) {
          this.setErrored("Audio context is null")
          throw Error("Audio context is null")
        }

        this._audioProcessorAdapterType =
          this.options.processorType == "auto"
            ? detectProcessorType(this._audioContext)
            : this.options.processorType

        switch (this._audioProcessorAdapterType) {
          case "AudioWorklet":
            {
              this._vadNode = await getVADNodeAsWorklet(
                this.options.baseAssetPath + workletFile,
                this.options.workletOptions,
                this._audioContext,
                this.options.frameSamples,
                this.emitFrame
              )
            }
            break

          case "ScriptProcessor":
            {
              this._vadNode = await getVADNodeAsScriptProcessor(
                this._audioContext,
                this.options.frameSamples,
                this.emitFrame
              )
            }
            break

          default: {
            throw new Error(
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              `Unsupported audio processor adapter type: ${this._audioProcessorAdapterType}`
            )
          }
        }

        this._mediaStreamAudioSourceNode = new MediaStreamAudioSourceNode(
          this._audioContext,
          {
            mediaStream: this._stream,
          }
        )
        this._mediaStreamAudioSourceNode.connect(this._vadNode)
        log.debug("started audio frame source")

        this.listening = true
        this.initializationState = "initialized"
        break
      }

      case "initializing": {
        log.warn("start called while initializing")
        break
      }

      case "initialized": {
        if (this.listening) {
          return
        }
        this.listening = true

        const { stream, audioContext, vadNode } = this.getAudioInstances()
        this._stream = await this.options.resumeStream(stream)

        const mediaStreamAudioSourceNode = new MediaStreamAudioSourceNode(
          audioContext,
          { mediaStream: this._stream }
        )
        this._mediaStreamAudioSourceNode = mediaStreamAudioSourceNode

        mediaStreamAudioSourceNode.connect(vadNode)
        break
      }

      case "destroyed": {
        log.warn("start called after destroyed")
        break
      }

      case "errored": {
        log.error("start called after errored")
        break
      }

      default: {
        log.warn("weird initialization state")
        break
      }
    }
  }

  pause = async () => {
    if (!this.listening) {
      return
    }
    this.listening = false

    const { stream, mediaStreamAudioSourceNode } = this.getAudioInstances()
    await this.options.pauseStream(stream)

    mediaStreamAudioSourceNode.disconnect()
  }

  destroy = async () => {
    log.debug("destroy called on audio frame source")
    this.initializationState = "destroyed"

    const { vadNode } = this.getAudioInstances()
    if (vadNode instanceof AudioWorkletNode) {
      vadNode.port.postMessage(Message.SpeechStop)
    }

    if (this.listening) {
      await this.pause()
    }
    if (this.ownsAudioContext) {
      await this._audioContext?.close()
    }
  }
}
